import { IMemberDetailsData } from 'fitbeat.gym-core';
import {
  Gym, IMemberColumnData, IOrganisationConfig, MembersReportData, ReportType,
} from 'fitbeat.models';
import moment from 'moment-timezone';
import * as React from 'react';
import { CSVLink } from 'react-csv';
import { connect } from 'react-redux';
import { Filter, SortingRule } from 'react-table';
import Modal from 'react-modal';
import { getSettings } from '../adminSettings/actions';
import { currentGymSelector } from '../adminSettings/selector';
import ColumnSelector from './components/columnSelector';
import ConfirmationModal from './components/confirmationModal';
import SaveReportModal from './components/saveReportModal';
import { deleteConfirmationActions, updateConfirmationActions } from './config';
import { convertDataToViews } from './storeTypes';
import { IAppStore } from '../rootReducer';
import { buildLoadingProgressMessage, Loading } from '../shared/loading';
import {
  clearFilterData,
  deleteReport,
  getAllData,
  getMonthlyPulseMembers,
  getMonthlyPulseSupport,
  getSavedReports,
  ILoadProgress,
  IWorkoutAggregateFilter,
  sendGeneralNotification,
  toggleConfirmationModal,
  toggleGeneralNotificationsInfoModal,
  toggleIsReportClone,
  toggleReportModal,
  updateReport,
  workoutAggregateDateChange,
} from './actions';
import { List } from './components/memberList';
import MemberReportHeaderReportSelection from './components/MemberReportHeaderReportSelection';
import ReportActionsDropdown from './components/ReportActionsDropdown';
import SimpleDateRangePickAndRun from './components/simpleDateRangePickAndRun';
import MessageActionsDropdown from './components/MessageActionsDropdown';
import { IMonthlyPulsePeriod, IPulseRunOptions } from './membersService';
import GeneralNotificationsModal from './components/generalNotificationsModal';
import MembersAnalyticsOverlay from './components/analytics/MembersAnalyticsOverlay';
import { AnalyticsMemberRow } from './components/analytics/membersAnalyticsLogic';
import { computeMonthlyPulseAggregates } from './components/monthly-pulse/monthlyPulseAggregates';
import { buildMonthlyPulseData } from './components/monthly-pulse/buildMonthlyPulseData';
import MonthlyPulseDashboard from './components/monthly-pulse/MonthlyPulseDashboard';
import MonthlyPulseEmptyState from './components/monthly-pulse/MonthlyPulseEmptyState';
import PulsePeriodPicker from './components/monthly-pulse/PulsePeriodPicker';
import { IMonthlyPulseVariables } from './components/monthly-pulse/MonthlyPulseVariables';
import {
  filterPulseMembersForRun, IPulsePeriodRange, resolvePulsePeriod,
} from './components/monthly-pulse/pulsePeriod';
import { toMonthlyPulseVariables } from './components/monthly-pulse/toMonthlyPulseVariables';
import '../styles/members/monthlyPulseModal.scss';

interface IProps {
  memberDetailsData: IMemberDetailsData[];
  excludedEmails: string;
  reportName: string;
  reportId: string;
  activeReport: MembersReportData;
  isNewReport: boolean;
  isDeleteConfirmation: boolean;
  memberColumnData: IMemberColumnData[];
  filtered: Filter[];
  sortOrder: SortingRule[];
  exportData: any[];
  isFetching: boolean;
  loadingMembers: boolean;
  currentGym: Gym;
  enableDataExport: boolean;
  lastViewedMemberReport: string;
  canSaveReports: boolean;
  canShowTestUsers: boolean;
  error: string | null;
  showConfirmationModal: boolean;
  showGeneralNotificationModal: boolean;
  workoutAggregateFilter: IWorkoutAggregateFilter;
  memberLoadProgress: ILoadProgress;
  organisationConfig?: IOrganisationConfig;
  clearFilterData(): void;
  getSettings(): void;
  getAllData(
    excludedEmails: string,
    gymName: string,
    workoutAggregateFilter?: IWorkoutAggregateFilter,
  ): void;
  toggleIsReportClone(): void;
  toggleReportModal(): void;
  toggleConfirmationModal(show: boolean): void;
  toggleGeneralNotificationsInfoModal(): void;
  sendGeneralNotification(userList: string[], notification: { title: string, message: string }): void;
  deleteReport(reportId: string): void;
  updateReport(): void;
  getSavedReports(reportType: ReportType): void;
  getMonthlyPulseSupport(
    gymName: string,
    period: IMonthlyPulsePeriod,
    runOptions?: IPulseRunOptions,
  ): Promise<any>;
  getMonthlyPulseMembers(
    excludedEmails: string,
    gymName: string,
    period: IMonthlyPulsePeriod,
  ): Promise<IMemberDetailsData[]>;
  workoutAggregateDateChange(data: any, gymName: string): void;
}

interface IState {
  showAnalyticsOverlay: boolean;
  showPulseDataOverlay: boolean;
  pulseVariables: IMonthlyPulseVariables | null;
  pulseDataError: string | null;
  pulseDataLoading: boolean;
}

export class Members extends React.Component<IProps, IState> {
  /** Invalidates in-flight pulse Generate runs when the modal is (re)opened. */
  private pulseRunId = 0;

  constructor(props: any) {
    super(props);
    this.state = {
      showAnalyticsOverlay: false,
      showPulseDataOverlay: false,
      pulseVariables: null,
      pulseDataError: null,
      pulseDataLoading: false,
    };
  }

  public componentDidMount() {
    if (this.props.memberDetailsData.length === 0) {
      this.props.getSavedReports(ReportType.members);
      this.props.getAllData(this.props.excludedEmails, this.props.currentGym.name);
    }
  }

  public setGetFilteredRowsForAnalytics = (getFilteredRows?: () => AnalyticsMemberRow[]) => {
    this.getFilteredRowsForAnalytics = getFilteredRows || (() => []);
  };

  public runStats = (data: IWorkoutAggregateFilter) => {
    this.props.workoutAggregateDateChange(data, this.props.currentGym.name);
    this.props.getAllData(
      this.props.excludedEmails,
      this.props.currentGym.name,
      data,
    );
  };

  public openAnalyticsOverlay = () => {
    this.setState({ showAnalyticsOverlay: true });
  };

  public closeAnalyticsOverlay = () => {
    this.setState({ showAnalyticsOverlay: false });
  };

  public closePulseDataOverlay = () => {
    this.setState({ showPulseDataOverlay: false });
  };

  public handlePulseDataClick = () => {
    this.pulseRunId += 1;
    this.setState({
      showPulseDataOverlay: true,
      pulseDataError: null,
      pulseDataLoading: false,
      pulseVariables: null,
    });
  };

  public handlePulseGenerate = async (range: IPulsePeriodRange, runOptions: IPulseRunOptions) => {
    const { currentGym, organisationConfig, excludedEmails } = this.props;
    const gymName = currentGym.name;
    const runId = this.pulseRunId;
    this.setState({ pulseDataLoading: true, pulseDataError: null });
    try {
      const { request, bounds, label } = resolvePulsePeriod(range, currentGym.timezone);
      const [pulseData, fetchedMembers] = await Promise.all([
        this.props.getMonthlyPulseSupport(gymName, request, runOptions),
        this.props.getMonthlyPulseMembers(excludedEmails, gymName, request),
      ]);
      if (runId !== this.pulseRunId) {
        return; // modal was reopened; this run's result belongs to a stale selection
      }
      const pulseMembers = filterPulseMembersForRun(fetchedMembers, runOptions);
      const pulseAggregates = computeMonthlyPulseAggregates(
        pulseMembers,
        gymName,
        label,
        bounds,
        pulseData.leaderboards.featured.joinerIds,
      );
      const pulseVariables = toMonthlyPulseVariables(
        buildMonthlyPulseData(pulseData, pulseAggregates, {
          gym: currentGym,
          organisationConfig,
          monthLabel: label,
        }),
      );
      this.setState({ pulseVariables, pulseDataLoading: false });
    } catch (e: any) {
      if (runId !== this.pulseRunId) {
        return;
      }
      this.setState({ pulseDataError: e.message || String(e), pulseDataLoading: false });
    }
  };

  public render() {
    const { canSaveReports } = this.props;
    const deleteTitles = deleteConfirmationActions(this.props.reportName);
    const updateTitles = updateConfirmationActions(this.props.reportName);
    return (
      <div className='app-wrapper'>
        <div className='table-action-header text-center'>
          <div className='row align-items-center report-top-section '>
            <div className='col'>
              <MemberReportHeaderReportSelection reportType={ReportType.members} />
            </div>
            <div className='row' style={{ alignItems: 'center' }}>
              {this.props.enableDataExport
                && (
                <div className='settings'>
                  <CSVLink
                    data={this.props.exportData || this.props.memberDetailsData}
                    asyncOnClick
                    filename={`${moment(new Date()).toISOString()}-member-table.csv`}
                    target='_blank'
                  >
                    <button className='jr-btn-no-border black-color-text'>
                      <img className='btnImg csvExportIconContainer' src='resources/images/csv-export-icon.svg' />
                      CSV Export
                    </button>
                  </CSVLink>
                </div>
                )}
              <div>
                <ColumnSelector />
              </div>
              <div className='workoutAggregateContainer' style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  className='jr-btn-no-border black-color-text'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0',
                    marginLeft: '10px',
                    marginRight: '4px',
                  }}
                >
                  <div className='workoutAggregateTextContainer' style={{ margin: 0 }}>
                    <div style={{ fontWeight: 400 }}>Stats</div>
                  </div>
                </div>
                <div style={{ alignItems: 'center', display: 'flex', marginRight: '0px' }}>
                  <SimpleDateRangePickAndRun
                    currentFilter={this.props.workoutAggregateFilter}
                    timezone={this.props.currentGym.timezone}
                    onRun={this.runStats}
                  />
                </div>
              </div>

              {canSaveReports && (
                <>
                  <MessageActionsDropdown
                    currentGym={this.props.currentGym}
                    toggleGeneralNotificationsModal={() => this.props.toggleGeneralNotificationsInfoModal()}
                  />
                  <ReportActionsDropdown
                    onAnalyticsClick={this.openAnalyticsOverlay}
                    onPulseDataClick={this.handlePulseDataClick}
                  />
                  <SaveReportModal />
                </>
              )}
            </div>
          </div>
          <GeneralNotificationsModal
            showGeneralNotificationsModal={this.props.showGeneralNotificationModal}
            onClose={() => this.props.toggleGeneralNotificationsInfoModal()}
            onCancel={() => this.props.toggleGeneralNotificationsInfoModal()}
            onYesClick={(header, body) => {
              const userList = this.props.exportData?.map(
                (member) => member.UID,
              ) || [];
              this.props.sendGeneralNotification(userList, {
                title: header,
                message: body,
              });
            }}
          />
          {this.props.isDeleteConfirmation
            ? (
              <ConfirmationModal
                error={this.props.error}
                isFetching={this.props.isFetching}
                showConfirmationModal={this.props.showConfirmationModal}
                titles={deleteTitles}
                onCancel={() => this.props.toggleConfirmationModal(false)}
                onClose={() => this.props.toggleConfirmationModal(false)}
                onYesClick={() => this.props.deleteReport(this.props.reportId)}
              />
            ) : (
              <ConfirmationModal
                error={this.props.error}
                isFetching={this.props.isFetching}
                showConfirmationModal={this.props.showConfirmationModal}
                titles={updateTitles}
                onCancel={() => this.props.toggleConfirmationModal(false)}
                onClose={() => this.props.toggleConfirmationModal(false)}
                onYesClick={() => this.props.updateReport()}
              />
            )}
          {this.props.memberDetailsData.length === 0 || this.props.loadingMembers
            ? (
              <div className='loader-container'>
                <Loading
                  fullHeight
                  message={buildLoadingProgressMessage(this.props.memberLoadProgress, 'members')}
                />
              </div>
            )
            : (
              <List
                report={this.props.activeReport}
                reportType={ReportType.members}
                onGetFilteredRowsChange={this.setGetFilteredRowsForAnalytics}
              />
            )}
          <MembersAnalyticsOverlay
            isOpen={this.state.showAnalyticsOverlay}
            gymDisplayName={this.props.currentGym.displayName || this.props.currentGym.name}
            organisationConfig={this.props.organisationConfig}
            rows={this.getFilteredRowsForAnalytics()}
            statsDateRange={this.props.workoutAggregateFilter}
            statsTimezone={this.props.currentGym.timezone}
            onClose={this.closeAnalyticsOverlay}
          />
          <Modal
            isOpen={this.state.showPulseDataOverlay}
            onRequestClose={this.closePulseDataOverlay}
            className='modal-content monthly-pulse-modal'
            overlayClassName='modal-overlay'
            ariaHideApp={false}
            contentLabel='Pulse Report'
          >
            <div className='monthly-pulse-modal-body'>
              <div className='monthly-pulse-modal-header'>
                <div className='monthly-pulse-modal-title'>
                  <h2>Pulse Report</h2>
                  <p className='monthly-pulse-modal-subtitle'>Select a date range to generate your report</p>
                </div>
                <button className='modal-close-button' onClick={this.closePulseDataOverlay}>X</button>
              </div>
              <PulsePeriodPicker
                timezone={this.props.currentGym.timezone}
                disabled={this.state.pulseDataLoading}
                canShowTestUsers={this.props.canShowTestUsers}
                onGenerate={this.handlePulseGenerate}
              />
              <div className='monthly-pulse-modal-scroll'>
                {this.state.pulseDataLoading && <div>Loading...</div>}
                {this.state.pulseDataError && <div style={{ color: 'red' }}>{this.state.pulseDataError}</div>}
                {!this.state.pulseDataLoading && (
                  this.state.pulseVariables
                    ? <MonthlyPulseDashboard variables={this.state.pulseVariables} />
                    : <MonthlyPulseEmptyState />
                )}
              </div>
            </div>
          </Modal>
        </div>
      </div>
    );
  }

  private getFilteredRowsForAnalytics: () => AnalyticsMemberRow[] = () => [];
}

const mapDispatchToProps = {
  getAllData,
  clearFilterData,
  toggleConfirmationModal,
  toggleReportModal,
  toggleIsReportClone,
  toggleGeneralNotificationsInfoModal,
  deleteReport,
  updateReport,
  getSettings,
  getSavedReports,
  getMonthlyPulseMembers,
  getMonthlyPulseSupport,
  workoutAggregateDateChange,
  sendGeneralNotification,
};
const mapStateToProps = (state: IAppStore) => {
  const { members, adminSettings } = state;
  const { canSaveReports, canShowTestUsers } = state.adminSettings;
  const {
    isNewReport, isClone, filtered, sortOrder, isDeleteConfirmation,
    error, isFetching, loadingMembers, showConfirmationModal, workoutAggregateFilter,
    showGeneralNotificationModal, memberLoadProgress,
  } = members.view;
  const {
    activeReport, memberDetailsData, lastViewedMemberReport, organisationConfig,
  } = members.data;
  const { exportData, enableDataExport } = adminSettings.data;
  const { memberColumnData, reportName, reportId } = members.data.activeReport;
  const currentGym = currentGymSelector(state);
  const memberColumnDataView = convertDataToViews(
    memberColumnData,
    state.members.data.filterDataFromMembersData,
    currentGym,
    state.members.data.onboardingQuestions,
  );
  const { excludedEmails } = adminSettings.data;
  return {
    memberTableColumns: memberColumnDataView.filter((column) => column.used),
    exportData,
    reportName,
    isNewReport,
    reportId,
    isClone,
    filtered,
    sortOrder,
    isDeleteConfirmation,
    error,
    isFetching,
    loadingMembers,
    activeReport,
    memberColumnData,
    memberDetailsData,
    excludedEmails,
    currentGym,
    enableDataExport,
    lastViewedMemberReport,
    showConfirmationModal,
    showGeneralNotificationModal,
    workoutAggregateFilter,
    memberLoadProgress,
    canSaveReports,
    canShowTestUsers,
    organisationConfig,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Members);
